import React, { useRef, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import { Button, Card, CardHeader, CardContent, CardActions, Grid, Divider, TextField, colors } from '@mui/material';
import { Page, Header, SelectField, FilePicker } from 'components';
import validate from 'validate.js';

import { enqueueSnackbar } from 'actions';
import { SetFormState, GenerateCellInfo } from '../../../actions/CellInfoActions.js';

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3),
	},
	container: {
		marginTop: theme.spacing(3),
	},
	tabs: {
		marginTop: theme.spacing(3),
	},
	content: {
		marginTop: theme.spacing(3),
	},
	generate: {
		color: theme.palette.white,
		backgroundColor: colors.green[600],
		'&:hover': {
			backgroundColor: colors.green[900],
		},
	},
}));

const schema = {
	sites: {
		presence: { allowEmpty: false, message: 'is required' },
	},
};

const TechAddForm = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.cellInfoReducer.formState.techAdd);

	useEffect(() => {
		const errors = validate(formState.values, schema);
		setFormState({
			techAdd: {
				...formState,
				isValid: !errors,
				errors: errors || {},
			}
		});
	}, [formState.values]);

	const _handleChange = (event) => {
		setFormState({
			techAdd: {
				...formState,
				values: {
					...formState.values,
					[event.target.name]:
						event.target.type === 'checkbox'
							? event.target.checked
							: event.target.value,
				},
				touched: {
					...formState.touched,
					[event.target.name]: true,
				},
			}
		});
	};

	const _handleChangeInputType = (event) => {
		setFormState({
			techAdd: {
				...formState,
				values: {
					...formState.values,
					sites: undefined,
					[event.target.name]:
						event.target.type === 'checkbox'
							? event.target.checked
							: event.target.value,
				},
				touched: {
					...formState.touched,
					[event.target.name]: true,
				},
			}
		});
	};

	const _hasError = (field) => (!!(formState.touched[field] && formState.errors[field]));

	const _onChangeInput = (event) => {
		if (event.target.files && event.target.files.length > 0) {
			const file = event.target.files[0];
			setFormState({
				techAdd: {
					...formState,
					values: {
						...formState.values,
						sites: file,
					},
					touched: {
						...formState.touched,
						sites: true,
					},
				}
			});
		}
	};

	const _generate = () => {
		if (formState.values.sites.size > 100000000) {
			dispatch(enqueueSnackbar({ message: 'File size cannot be superior to 100MB', options: { variant: 'warning' } }));
			return;
		}

		dispatch(GenerateCellInfo(formState.values.inputType, 'techAdd', formState.values.sites));
	};

	return (
		<Card>
			<CardHeader title="Tech Add" />
			<Divider />
			<CardContent>
				<Grid container spacing={1}>
					<Grid item md={2} xs={12}>
						<SelectField
							id="inputType"
							name="inputType"
							value={formState.values.inputType || ''}
							onChange={_handleChangeInputType}
							hintText="Input Type"
							menuItems={[{ value: 'file', text: 'File' }, { value: 'text', text: 'Text' }]}
							helperText={
								_hasError('inputType') ? formState.errors.inputType[0] : null
							}
						/>
					</Grid>
					<Grid item md={4} xs={12}>
						{(formState.values.inputType || 'file') === 'file'
							? <FilePicker value={formState.values.sites?.name} onChange={_onChangeInput} accept=".txt" />
							:				(
								<TextField
									fullWidth
									label="Sites"
									name="sites"
									onChange={_handleChange}
									value={formState.values.sites}
									variant="outlined"
									error={_hasError('sites')}
									helperText={
										_hasError('sites') ? formState.errors.sites[0] : 'Example: site1,site2,site3'
									}
								/>
							)}
					</Grid>
				</Grid>

			</CardContent>
			<Divider />
			<CardActions>
				<Button className={classes.generate} onClick={_generate} variant="contained" disabled={!formState.isValid}>Generate</Button>
			</CardActions>
		</Card>
	);
};

export default TechAddForm;
